/* eslint-disable */
import React, { useState } from 'react';
import { useRecordContext } from 'react-admin';
import { useMutation, useQuery } from '@tanstack/react-query';
import { fetchCdpJson, cdpRetry } from '../../../helpers/cdp';
import { ensureOk } from '../../../helpers/http';

const CARE_DATA_PARSER_BADGES_URL = '/cdp/badges';
// Relais serveur du backoffice (src/server/timeClockProxy.js, plan 018) : il
// exige la session care, puis ajoute la clé de service et l'auteur avant
// d'appeler la Lambda badge-sync. Le navigateur ne voit ni l'une ni l'autre.
const BADGE_SYNC_URL = '/time-clock/badge-sync';

export const normalizePin = (pin) => {
  const cleaned = pin.replace(/[^0-9A-F]/gi, '').toUpperCase();
  if (cleaned.length !== 14) {
    throw new Error('PIN doit faire 14 caractères HEX');
  }
  return cleaned;
};

// Entrée dans le champ de recherche soumettrait le formulaire react-admin
// qui contient ce composant (enregistrement de toute la fiche).
const preventEnterSubmit = (e) => {
  if (e.key === 'Enter') e.preventDefault();
};

const assignBadgeToCleaner = async (pin, label, externalReferences) => {
  return await fetch(BADGE_SYNC_URL, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      pin,
      label,
      externalReferences,
    }),
  }).then(ensureOk);
};

const removeBadgeFromCleaner = async (pin, label) =>
  await fetch(BADGE_SYNC_URL, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      pin,
      label,
      externalReferences: {},
    }),
  }).then(ensureOk);

export function TimeClockBadgeInput() {
  const record = useRecordContext();
  const [selectedBadgePin, setSelectedBadgePin] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const silaeId = (record?.silae_id || '').replace(/^0+/, '');
  const { data: badgesList, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['badges', record?.id],
    queryFn: async () => {
      return fetchCdpJson(CARE_DATA_PARSER_BADGES_URL);
    },
    enabled: !!record?.id,
    retry: cdpRetry,
    select: (data) => {
      const list = Array.isArray(data) ? data : [];
      return list.filter(
        (b) =>
          (b.label || '').toLowerCase().includes(searchValue) ||
          (b.pin || '').toLowerCase().includes(searchValue),
      );
    },
  });

  const updateAssignedBadgeMutation = useMutation({
    mutationFn: () =>
      assignBadgeToCleaner(normalizePin(selectedBadgePin || searchValue), record?.fullname, {
        CleanerId: record?.id,
        FirstName: record?.firstname || '',
        LastName: record?.lastname || '',
        Position: record?.function || '',
        SilaeId: silaeId || '',
      }),
    onSuccess: () => {
      refetch();
      setStatusMessage('✔ Badge assigné');
    },
    onError: (e) => {
      setStatusMessage('❌ ' + e.message);
    },
  });

  const removeAssignedBadgeMutation = useMutation({
    mutationFn: (pin) => removeBadgeFromCleaner(pin, record?.fullname),
    onSuccess: () => {
      refetch();
      setStatusMessage('✔ Badge retiré');
    },
    onError: (e) => {
      setStatusMessage('❌ ' + e.message);
    },
  });
  const [statusMessage, setStatusMessage] = useState('');

  const currentAssignedBadge = (badgesList || []).find(
    (badge) => badge.externalReferences?.CleanerId === record?.id,
  );

  if (isLoading) return <p>Chargement...</p>;
  if (isError)
    return (
      <p style={{ color: '#d9534f' }}>
        {error?.message || 'Erreur lors du chargement des badges'}
      </p>
    );

  return (
    <div style={styles.container}>
      <div style={currentAssignedBadge ? styles.boxOK : styles.boxWarn}>
        {currentAssignedBadge ? (
          <>
            <strong>Badge actuel :</strong> <br />
            {currentAssignedBadge.label} <br />({currentAssignedBadge.pin})
          </>
        ) : (
          '⚠ Aucun badge associé'
        )}
      </div>

      <input
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onKeyDown={preventEnterSubmit}
        placeholder="Rechercher un badge..."
        style={styles.input}
      />

      <select
        value={selectedBadgePin}
        onChange={(e) => setSelectedBadgePin(e.target.value)}
        style={styles.select}
      >
        <option value="">-- Sélectionner --</option>
        {(badgesList || []).map((badge) => (
          <option key={badge.pin} value={badge.pin}>
            {badge.label} ({badge.pin})
          </option>
        ))}
      </select>

      <div style={styles.actions}>
        <button
          type="button"
          style={styles.btnPrimary(updateAssignedBadgeMutation.isLoading)}
          disabled={
            updateAssignedBadgeMutation.isLoading ||
            (!selectedBadgePin && !searchValue)
          }
          onClick={() => updateAssignedBadgeMutation.mutate()}
        >
          {updateAssignedBadgeMutation.isLoading ? '...' : 'Assigner'}
        </button>

        <button
          type="button"
          style={styles.btnDanger(removeAssignedBadgeMutation.isLoading)}
          disabled={
            !currentAssignedBadge || removeAssignedBadgeMutation.isLoading
          }
          onClick={() => removeAssignedBadgeMutation.mutate(currentAssignedBadge.pin)}
        >
          {removeAssignedBadgeMutation.isLoading ? '...' : 'Retirer'}
        </button>
      </div>

      {statusMessage && (
        <p
          style={{
            ...styles.message,
            background: statusMessage.startsWith('❌') ? '#ffe5e5' : '#e6f9ec',
            color: statusMessage.startsWith('❌') ? '#d9534f' : '#2d8a4e',
          }}
        >
          {statusMessage}
        </p>
      )}
    </div>
  );
}

export default TimeClockBadgeInput;

const styles = {
  container: {
    marginTop: 20,
    fontFamily: 'system-ui',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  boxOK: {
    background: '#e6f9ec',
    padding: '14px 18px',
    borderRadius: 10,
    marginBottom: 12,
    fontSize: 16,
    width: '80%',
    maxWidth: 480,
    textAlign: 'center',
  },
  boxWarn: {
    background: '#fff8e1',
    padding: '14px 18px',
    borderRadius: 10,
    marginBottom: 12,
    fontSize: 16,
    width: '80%',
    maxWidth: 480,
    textAlign: 'center',
  },
  input: {
    width: '80%',
    maxWidth: 480,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #bbb',
    marginBottom: 12,
    fontSize: 16,
    textTransform: 'uppercase',
  },
  select: {
    width: '80%',
    maxWidth: 480,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #bbb',
    marginBottom: 16,
    fontSize: 16,
    background: '#fff',
  },
  actions: {
    display: 'flex',
    gap: 16,
    width: '80%',
    maxWidth: 480,
    justifyContent: 'center',
  },
  btnPrimary: (loading) => ({
    flex: 1,
    padding: '12px 0',
    background: loading ? '#aaa' : '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    cursor: loading ? 'not-allowed' : 'pointer',
    fontSize: 16,
    fontWeight: 600,
  }),
  btnDanger: (loading) => ({
    flex: 1,
    padding: '12px 0',
    background: loading ? '#aaa' : '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    cursor: loading ? 'not-allowed' : 'pointer',
    fontSize: 16,
    fontWeight: 600,
  }),
  message: {
    marginTop: 16,
    padding: '10px 16px',
    borderRadius: 10,
    width: '80%',
    maxWidth: 480,
    fontSize: 16,
    textAlign: 'center',
    fontWeight: 500,
  },
};
